import { Box, Card, Pagination } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SearchAccountsSort, UserPermissions } from 'types';

import usePermissions from '../hooks/usePermissions';
import { useGetAccountListFiltersQuery, useLazyGetAccountsSummaryQuery } from '../services/accounts';

import { AccountsListLoading } from './AccountsListLoading';
import { AccountsSummaryTable } from './AccountsSummaryTable';
import { FilterBar } from './filters';
import { AddFilterButtonDropdown } from './filters/AddFilterButton';
import { AppliedDataFilter } from './filters/FilterButton';
import { SortButtonDropdown } from './filters/SortButton';
import { MilestoneTabs } from './MilestoneTabs';

const sortOptions: { id: string; label: string }[] = [
  { id: SearchAccountsSort.Newest, label: 'Age - New to Old' },
  {
    id: SearchAccountsSort.Oldest,
    label: 'Age - Old to New',
  },
  { id: SearchAccountsSort.NewestUpdates, label: 'Updated - New to Old' },
  {
    id: SearchAccountsSort.OldestUpdates,
    label: 'Updated - Old to New',
  },
];

export function AccountsSummary() {
  const [trigger, { data: summaryResponse, isLoading, isFetching }] = useLazyGetAccountsSummaryQuery();
  const [appliedFilters, setAppliedFilters] = useState<Array<any>>([]);
  const userPermissions = usePermissions();
  const {
    accounts,
    milestoneSummary,
    total: accountsTotal,
  } = summaryResponse || { accounts: [], milestoneSummary: {}, total: 0 };
  const [searchParams, setSearchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled');
  const postActivation = searchParams.get('postActivation');
  const currentMilestone = searchParams.get('currentMilestone');
  const currentPage = searchParams.get('page');
  const advancedFilters = searchParams.get('advancedFilters');
  const searchTerm = searchParams.get('search') || undefined;
  const sort = (searchParams.get('sort') as SearchAccountsSort) || SearchAccountsSort.Newest;
  const appliedSort = sort ? sortOptions.find((opt) => opt.id === sort) : sortOptions[0];
  const showCancelled = cancelled !== null;
  const showPostActivation = postActivation !== null;
  const { data = [], isLoading: isFiltersLoading } = useGetAccountListFiltersQuery({
    cancelled: showCancelled,
    includeOptions: false,
  });

  useEffect(() => {
    if (showCancelled && userPermissions.length) {
      if (!userPermissions.includes(UserPermissions.admin)) {
        searchParams.delete('cancelled');
        setSearchParams(searchParams);
      }
    }
  }, [showCancelled, userPermissions, searchParams, setSearchParams]);

  useEffect(() => {
    const milestone = currentMilestone && currentMilestone !== 'undefined' ? currentMilestone : undefined;
    const pageNum = currentPage ? Number(currentPage) : 1;
    const filters = advancedFilters || JSON.stringify([]);
    const parsedFilters = JSON.parse(filters);
    const agingFilterEnabled = parsedFilters.find((filter: AppliedDataFilter) => filter.id === 'agingAccounts');

    setAppliedFilters(parsedFilters);
    trigger({
      currentMilestone: milestone,
      pageNum,
      advancedFilters: filters,
      searchTerm,
      includeCompletedAccounts: showPostActivation || !!agingFilterEnabled,
      onlyCancelledAccounts: showCancelled,
      onlyPostActivationAccounts: showPostActivation,
      sort: appliedSort?.id,
    });
  }, [
    trigger,
    appliedSort,
    currentMilestone,
    currentPage,
    advancedFilters,
    searchTerm,
    showCancelled,
    showPostActivation,
  ]);

  const handleApplyAdvancedFilters = useCallback(
    (filtersToApply: AppliedDataFilter[]) => {
      if (filtersToApply.some((filter) => filter.selectedValues && filter.selectedValues.length)) {
        searchParams.set('advancedFilters', JSON.stringify(filtersToApply));
        setSearchParams(searchParams);
      }
      searchParams.delete('page');
      setAppliedFilters(filtersToApply);
    },
    [setAppliedFilters, searchParams, setSearchParams],
  );

  const handleRemoveAdvancedFilter = useCallback(
    (filterId: string) => {
      const newFilters = appliedFilters.filter((filter) => filter.id !== filterId);
      if (newFilters.length) {
        searchParams.set('advancedFilters', JSON.stringify(newFilters));
      } else {
        searchParams.delete('advancedFilters');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
      setAppliedFilters(newFilters);
    },
    [appliedFilters, setAppliedFilters, searchParams, setSearchParams],
  );

  const handleClearAdvancedFilters = useCallback(() => {
    searchParams.delete('advancedFilters');
    searchParams.delete('page');
    setSearchParams(searchParams);
    setAppliedFilters([]);
  }, [setAppliedFilters, searchParams, setSearchParams]);

  const onPageChange = (newPage: number) => {
    searchParams.set('page', newPage.toString());
    setSearchParams(searchParams);
  };

  const onSearchChange = (searchTerm: string) => {
    if (searchTerm && searchTerm !== '') {
      searchParams.set('search', searchTerm);
    } else {
      searchParams.delete('search');
    }
    searchParams.delete('page');
    setSearchParams(searchParams);
  };

  const handleSortSelection = useCallback(
    (sortSelection: { id: string; label: string }) => {
      searchParams.set('sort', sortSelection.id);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const updatedAccountFilters = useMemo(() => {
    return showPostActivation ? data.filter((filter: any) => filter?.id !== 'status') : data;
  }, [data, showPostActivation]);

  const handleSelectMilestone = useCallback(
    (type?: string) => {
      if (type) {
        searchParams.set('currentMilestone', type);
      } else {
        searchParams.delete('currentMilestone');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  return (
    <Card>
      <MilestoneTabs
        milestoneSummary={milestoneSummary}
        currentMilestone={currentMilestone}
        isLoading={isLoading}
        onSelectMilestone={handleSelectMilestone}
      />
      <Box margin="0 lg 0 lg">
        <FilterBar
          filtersState={{
            searchTerm,
          }}
          handleSearchTerm={(searchTerm: string) => onSearchChange(searchTerm)}
          placeholder="Search by name, email, address..."
        >
          <Box direction="row" style={{ flexGrow: 1 }}>
            {!isFiltersLoading && (
              <AddFilterButtonDropdown
                dataFilters={updatedAccountFilters}
                appliedFilters={appliedFilters}
                onRemoveFilter={handleRemoveAdvancedFilter}
                onApplyFilters={handleApplyAdvancedFilters}
                onClearFilters={handleClearAdvancedFilters}
                lazyFetchOptions
              />
            )}
            <SortButtonDropdown
              appliedSort={appliedSort}
              sortOptions={sortOptions}
              onSortChange={handleSortSelection}
              className="sortButton"
            />
          </Box>
        </FilterBar>
        {isFetching || isLoading ? <AccountsListLoading rows={12} /> : <AccountsSummaryTable data={accounts} />}
      </Box>
      {accounts?.length > 0 && (
        <Pagination
          activePage={Number(currentPage) || 1}
          arePagesVisible
          itemsPerPage={20}
          numberOfPagesDisplayed={3}
          onChange={(page) => onPageChange(page)}
          totalItemsCount={accountsTotal ?? 0}
          isCompact
        />
      )}
    </Card>
  );
}
